import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SpecraCheckbox, SpecraCombobox, SpecraDateField, SpecraFormField, SpecraInput, SpecraOverlayFooter, SpecraText, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useEffect, useRef, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { KpiInfo } from '../../../../components/delivery/widgets/KpiTile';
import { entityErrorMessage } from '../lib/entityErrorMessage';
/**
 * The declarative delivery form body: react-hook-form wiring, FormFieldConfig
 * rendering, inline submit-error + progress, and a pinned SpecraOverlayFooter.
 * It owns the scroll split — fields scroll, the footer stays pinned inside the
 * same <form> so Save is always reachable — so its host must be a bounded flex
 * column (EntityFormDialog's dialog body; the detail drawer's edit panel).
 */ export function EntityForm({ fields, defaultValues, onSubmit, onCancel, isSubmitting, submitLabel = 'Save', topContent, extraContent, readOnly, active = true }) {
    // mode: 'onTouched' validates each field when it first loses focus, then
    // re-validates on every change — so a required-but-empty field highlights
    // after the user tabs away, and clears the moment they type something valid.
    const { control, handleSubmit, reset, formState, watch, getValues, setValue } = useForm({
        defaultValues: defaultValues,
        mode: 'onTouched'
    });
    // Subscribes to every live value so a field's `hiddenWhen` re-evaluates as
    // the user types/picks — plain `watch` above doesn't trigger a re-render.
    const liveValues = useWatch({
        control
    });
    const visibleFields = fields.filter((field)=>!field.hiddenWhen?.(liveValues));
    // Persistent inline surface for a server-side submit rejection. A toast (wired
    // in the consumer's useMutation onError) auto-dismisses, but the host stays
    // open on failure — so without an in-context message the user sees a form
    // that "did nothing". Held here and rendered above the footer.
    const [submitError, setSubmitError] = useState(null);
    // Reset only on the active false→true edge. defaultValues is typically a fresh
    // object literal each render (callers spread `editing?.*`), so depending on it
    // would re-run on every parent re-render and wipe in-flight user input.
    const prevActive = useRef(false);
    useEffect(()=>{
        if (active && !prevActive.current) {
            reset(defaultValues);
            setSubmitError(null);
        }
        prevActive.current = active;
    }, [
        active,
        defaultValues,
        reset
    ]);
    // Field names cross the type-erased boundary (the form runs on
    // Record<string, unknown>), so the RHF path types are widened here — the
    // caller supplies keys of its own T.
    const formAccess = {
        watch: (name)=>watch(name),
        getValues: (name)=>getValues(name),
        setValue: (name, value)=>setValue(name, value)
    };
    return /*#__PURE__*/ _jsxs("form", {
        onSubmit: handleSubmit(async (data)=>{
            if (readOnly) return;
            // Swallow rejections from onSubmit so react-hook-form doesn't re-raise
            // them into an unhandled promise rejection that crashes the React tree
            // (server-side guards like "cannot create a task in Closed state"). The
            // consumer's close lives after the mutateAsync await, so a throw already
            // skips it — the host correctly stays open on failure. We ALSO surface
            // the message inline (below).
            setSubmitError(null);
            try {
                await onSubmit(data);
            } catch (err) {
                setSubmitError(entityErrorMessage(err));
            }
        }),
        className: "flex min-h-0 flex-1 flex-col",
        children: [
            /*#__PURE__*/ _jsx("fieldset", {
                disabled: readOnly,
                className: "contents",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "min-h-0 flex-1 space-y-4 overflow-y-auto pr-1",
                    children: [
                        typeof topContent === 'function' ? topContent(formAccess) : topContent,
                        visibleFields.map((field)=>/*#__PURE__*/ _jsx(FieldRow, {
                                field: field,
                                control: control,
                                errors: formState.errors
                            }, field.name)),
                        typeof extraContent === 'function' ? extraContent(formAccess) : extraContent
                    ]
                })
            }),
            submitError && /*#__PURE__*/ _jsx("p", {
                className: "mt-4 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive",
                role: "alert",
                "data-testid": Testids.deliveryShared.formError,
                children: submitError
            }),
            isSubmitting && // No aria-valuenow on purpose: a progressbar without a value is the ARIA
            // indeterminate state — we don't know how long the mutation will take.
            /*#__PURE__*/ _jsx("div", {
                role: "progressbar",
                "aria-label": "Saving…",
                "data-testid": Testids.deliveryShared.formProgress,
                className: "mt-4 h-1.5 w-full overflow-hidden rounded-full bg-primary/20",
                children: /*#__PURE__*/ _jsx("div", {
                    className: "h-full w-full animate-shimmer bg-gradient-to-r from-transparent via-primary to-transparent bg-[length:200%_100%]"
                })
            }),
            /*#__PURE__*/ _jsx(SpecraOverlayFooter, {
                // Stretch to the host edges so the divider spans full width even though
                // the body wrapper carries the horizontal padding.
                className: "-mx-4 -mb-3 mt-4 sm:-mx-5 sm:-mb-4",
                loading: isSubmitting,
                loadingLabel: "Saving…",
                confirmLabel: submitLabel,
                confirmDisabled: isSubmitting || readOnly,
                onCancel: onCancel,
                cancelProps: {
                    disabled: isSubmitting,
                    'data-testid': Testids.deliveryShared.formCancel
                },
                confirmProps: {
                    type: 'submit',
                    'data-testid': Testids.deliveryShared.formSubmit
                }
            })
        ]
    });
}
/**
 * The bare input for a FormFieldConfig — the same atoms and validation contract
 * as {@link FieldRow}, but with no SpecraFormField label/hint wrapper. It's the
 * value-cell control for the detail drawer's inline edit, where the read row's
 * `<dt>` already supplies the label (wired via `labelledBy`). The create modal
 * keeps FieldRow; only inline edit uses this.
 */ export function FieldControl({ field, control, errors, labelledBy }) {
    const fieldName = field.name;
    const id = String(field.name);
    const error = errors[fieldName];
    const hasError = Boolean(error);
    const errorMessage = typeof error?.message === 'string' ? error.message : undefined;
    const invalidClass = hasError ? 'border-destructive focus-visible:ring-destructive/30' : '';
    // Same id contract as SpecraFormField's hint/error slots, recreated here
    // since this control renders without that wrapper (the row's <dt> is the
    // label instead) — see the aria-describedby wiring below.
    const hintId = field.helpText ? `${id}-hint` : undefined;
    const errorId = errorMessage ? `${id}-error` : undefined;
    const describedBy = [
        hintId,
        errorId
    ].filter(Boolean).join(' ') || undefined;
    return /*#__PURE__*/ _jsx(Controller, {
        control: control,
        name: fieldName,
        rules: {
            required: field.required ? `${field.label} is required` : false
        },
        render: ({ field: rhf })=>{
            // The calendar field carries its own hint + error slot (via
            // SpecraFormField), so it renders standalone with a visually-hidden
            // label (the <dt> is the visible label) and reports its own inline
            // hint/error rather than double-rendering them below.
            if (field.kind === 'date') {
                return /*#__PURE__*/ _jsx(SpecraDateField, {
                    id: id,
                    required: false,
                    label: /*#__PURE__*/ _jsx("span", {
                        className: "sr-only",
                        children: field.label
                    }),
                    hint: field.helpText,
                    error: errorMessage,
                    value: rhf.value ?? '',
                    onValueChange: (next)=>rhf.onChange(next),
                    onBlur: rhf.onBlur,
                    "data-testid": Testids.deliveryShared.formField,
                    "data-name": id
                });
            }
            const controlNode = (()=>{
                switch(field.kind){
                    case 'text':
                        return /*#__PURE__*/ _jsx(SpecraInput, {
                            id: id,
                            "aria-labelledby": labelledBy,
                            "aria-describedby": describedBy,
                            required: false,
                            invalid: hasError,
                            value: rhf.value ?? '',
                            onChange: (e)=>rhf.onChange(e.target.value),
                            onBlur: rhf.onBlur,
                            placeholder: field.placeholder,
                            maxLength: field.maxLength,
                            list: field.suggestions ? `${id}-suggestions` : undefined,
                            "data-testid": field.testId ?? Testids.deliveryShared.formField,
                            "data-name": id
                        });
                    case 'textarea':
                        return /*#__PURE__*/ _jsx(SpecraTextarea, {
                            id: id,
                            "aria-labelledby": labelledBy,
                            "aria-describedby": describedBy,
                            required: false,
                            invalid: hasError,
                            value: rhf.value ?? '',
                            onChange: (e)=>rhf.onChange(e.target.value),
                            onBlur: rhf.onBlur,
                            rows: field.rows ?? 3,
                            placeholder: field.placeholder,
                            "data-testid": Testids.deliveryShared.formField,
                            "data-name": id
                        });
                    case 'number':
                        return /*#__PURE__*/ _jsx(SpecraInput, {
                            id: id,
                            "aria-labelledby": labelledBy,
                            "aria-describedby": describedBy,
                            required: false,
                            invalid: hasError,
                            type: "number",
                            disabled: field.disabled,
                            value: rhf.value ?? '',
                            onChange: (e)=>rhf.onChange(e.target.value === '' ? undefined : Number(e.target.value)),
                            onBlur: rhf.onBlur,
                            min: field.min,
                            max: field.max,
                            step: field.step,
                            "data-testid": Testids.deliveryShared.formField,
                            "data-name": id
                        });
                    case 'month':
                        return /*#__PURE__*/ _jsx(SpecraInput, {
                            id: id,
                            "aria-labelledby": labelledBy,
                            "aria-describedby": describedBy,
                            required: false,
                            invalid: hasError,
                            type: "month",
                            value: rhf.value ?? '',
                            onChange: (e)=>rhf.onChange(e.target.value),
                            onBlur: rhf.onBlur,
                            "data-testid": field.testId ?? Testids.deliveryShared.formField,
                            "data-name": id
                        });
                    case 'currency':
                        return /*#__PURE__*/ _jsx(SpecraInput, {
                            id: id,
                            "aria-labelledby": labelledBy,
                            "aria-describedby": describedBy,
                            required: false,
                            invalid: hasError,
                            type: "number",
                            value: rhf.value ?? '',
                            onChange: (e)=>rhf.onChange(e.target.value === '' ? undefined : Number(e.target.value)),
                            onBlur: (e)=>{
                                if (e.target.value === '') {
                                    rhf.onBlur();
                                    return;
                                }
                                rhf.onChange(Math.round(Number(e.target.value) * 100) / 100);
                                rhf.onBlur();
                            },
                            min: field.min,
                            max: field.max,
                            step: ".01",
                            "data-testid": Testids.deliveryShared.formField,
                            "data-name": id
                        });
                    case 'select':
                        if (field.searchable) {
                            return /*#__PURE__*/ _jsx(SpecraCombobox, {
                                id: id,
                                label: field.label,
                                options: [
                                    ...field.options
                                ],
                                value: rhf.value || undefined,
                                onValueChange: (v)=>{
                                    rhf.onChange(v ?? '');
                                    rhf.onBlur();
                                },
                                // Touched when it closes without a pick too, as the plain select is.
                                onOpenChange: (open)=>{
                                    if (!open) rhf.onBlur();
                                },
                                placeholder: "Select...",
                                searchPlaceholder: `Search ${field.label.toLowerCase()}…`,
                                triggerProps: {
                                    'aria-labelledby': labelledBy,
                                    'aria-describedby': describedBy,
                                    'aria-invalid': hasError || undefined,
                                    className: invalidClass,
                                    'data-testid': field.testId ?? Testids.deliveryShared.formField,
                                    'data-name': id
                                }
                            });
                        }
                        return /*#__PURE__*/ _jsxs(Select, {
                            required: false,
                            value: rhf.value ?? '',
                            onValueChange: (v)=>{
                                rhf.onChange(v);
                                rhf.onBlur();
                            },
                            onOpenChange: (open)=>{
                                if (!open) rhf.onBlur();
                            },
                            children: [
                                /*#__PURE__*/ _jsx(SelectTrigger, {
                                    id: id,
                                    "aria-labelledby": labelledBy,
                                    "aria-describedby": describedBy,
                                    "aria-invalid": hasError || undefined,
                                    className: invalidClass,
                                    "data-testid": field.testId ?? Testids.deliveryShared.formField,
                                    "data-name": id,
                                    children: /*#__PURE__*/ _jsx(SelectValue, {
                                        placeholder: "Select..."
                                    })
                                }),
                                /*#__PURE__*/ _jsx(SelectContent, {
                                    children: field.options.map((opt)=>/*#__PURE__*/ _jsx(SelectItem, {
                                            value: opt.value,
                                            children: opt.label
                                        }, opt.value))
                                })
                            ]
                        });
                    case 'checkbox':
                        return /*#__PURE__*/ _jsx(SpecraCheckbox, {
                            id: id,
                            "aria-labelledby": labelledBy,
                            "aria-describedby": describedBy,
                            checked: Boolean(rhf.value),
                            onCheckedChange: (checked)=>rhf.onChange(Boolean(checked)),
                            "aria-invalid": hasError || undefined,
                            className: hasError ? 'border-destructive focus-visible:ring-destructive/30 data-[state=checked]:border-destructive data-[state=unchecked]:border-destructive' : '',
                            "data-testid": Testids.deliveryShared.formField,
                            "data-name": id
                        });
                }
            })();
            return /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    controlNode,
                    field.helpText && /*#__PURE__*/ _jsx(SpecraText, {
                        as: "p",
                        id: hintId,
                        size: "caption",
                        tone: "muted",
                        className: "mt-1",
                        children: field.helpText
                    }),
                    errorMessage && /*#__PURE__*/ _jsx("p", {
                        id: errorId,
                        className: "mt-1 text-[11px] text-danger",
                        role: "alert",
                        children: errorMessage
                    }),
                    field.kind === 'text' && field.suggestions && /*#__PURE__*/ _jsx("datalist", {
                        id: `${id}-suggestions`,
                        children: field.suggestions.map((s)=>/*#__PURE__*/ _jsx("option", {
                                value: s
                            }, s))
                    }),
                    field.kind === 'select' && field.optionHelp && // Lives outside the Select/SelectContent tree on purpose — see
                    // the optionHelp comment on SelectFieldConfig.
                    /*#__PURE__*/ _jsx("dl", {
                        className: "mt-1 space-y-0.5 text-xs text-muted-foreground",
                        "data-testid": field.optionHelpTestId,
                        children: field.optionHelp.map((entry)=>/*#__PURE__*/ _jsxs("div", {
                                className: "flex gap-1",
                                children: [
                                    /*#__PURE__*/ _jsxs("dt", {
                                        className: "font-medium",
                                        children: [
                                            entry.label,
                                            ":"
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx("dd", {
                                        children: entry.text
                                    })
                                ]
                            }, entry.label))
                    })
                ]
            });
        }
    });
}
export function FieldRow({ field, control, errors }) {
    const fieldName = field.name;
    const id = String(field.name);
    const error = errors[fieldName];
    const hasError = Boolean(error);
    const errorMessage = typeof error?.message === 'string' ? error.message : undefined;
    // Red border on invalid selects — the branded atoms take `invalid` instead.
    const invalidClass = hasError ? 'border-destructive focus-visible:ring-destructive/30' : '';
    return /*#__PURE__*/ _jsx(Controller, {
        control: control,
        name: fieldName,
        rules: {
            required: field.required ? `${field.label} is required` : false
        },
        render: ({ field: rhf })=>{
            // The calendar date picker carries its own SpecraFormField (label, hint,
            // error), so it renders standalone instead of nesting inside the shared
            // wrapper below (which would emit a second, empty label). Keep the
            // control non-required so native constraint validation can't intercept
            // submit before react-hook-form runs, and fold the required asterisk
            // into the label so required date fields still read as required.
            if (field.kind === 'date') {
                return /*#__PURE__*/ _jsx(SpecraDateField, {
                    id: id,
                    required: false,
                    label: field.required ? /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            field.label,
                            /*#__PURE__*/ _jsx("span", {
                                "aria-hidden": "true",
                                className: "ml-0.5 text-danger",
                                children: "*"
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: "sr-only",
                                children: " (required)"
                            })
                        ]
                    }) : field.label,
                    hint: field.helpText,
                    error: errorMessage,
                    value: rhf.value ?? '',
                    onValueChange: (next)=>rhf.onChange(next),
                    onBlur: rhf.onBlur,
                    "data-testid": Testids.deliveryShared.formField,
                    "data-name": id
                });
            }
            return /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(SpecraFormField, {
                        label: field.label,
                        labelAdornment: field.infoTip ? /*#__PURE__*/ _jsx(KpiInfo, {
                            label: field.label,
                            tip: field.infoTip,
                            testid: Testids.deliveryShared.infoTip,
                            dataName: id
                        }) : undefined,
                        htmlFor: id,
                        required: field.required,
                        // Error text takes precedence over the hint, same as before; a
                        // validation error without a message still marks the control
                        // invalid via the atom's `invalid` prop below.
                        error: errorMessage,
                        hint: field.helpText,
                        "data-testid": Testids.deliveryShared.formField,
                        "data-name": id,
                        children: (()=>{
                            switch(field.kind){
                                case 'text':
                                    return /*#__PURE__*/ _jsx(SpecraInput, {
                                        id: id,
                                        // required={false} everywhere: validation is RHF's job —
                                        // SpecraFormField clones `required` onto its control, and
                                        // native browser validation would intercept submit before
                                        // react-hook-form sees it.
                                        required: false,
                                        invalid: hasError,
                                        value: rhf.value ?? '',
                                        onChange: (e)=>rhf.onChange(e.target.value),
                                        onBlur: rhf.onBlur,
                                        placeholder: field.placeholder,
                                        maxLength: field.maxLength,
                                        list: field.suggestions ? `${id}-suggestions` : undefined,
                                        "data-testid": field.testId
                                    });
                                case 'textarea':
                                    return /*#__PURE__*/ _jsx(SpecraTextarea, {
                                        id: id,
                                        required: false,
                                        invalid: hasError,
                                        value: rhf.value ?? '',
                                        onChange: (e)=>rhf.onChange(e.target.value),
                                        onBlur: rhf.onBlur,
                                        rows: field.rows ?? 3,
                                        placeholder: field.placeholder
                                    });
                                case 'number':
                                    return /*#__PURE__*/ _jsx(SpecraInput, {
                                        id: id,
                                        required: false,
                                        invalid: hasError,
                                        type: "number",
                                        disabled: field.disabled,
                                        value: rhf.value ?? '',
                                        onChange: (e)=>rhf.onChange(e.target.value === '' ? undefined : Number(e.target.value)),
                                        onBlur: rhf.onBlur,
                                        min: field.min,
                                        max: field.max,
                                        step: field.step
                                    });
                                case 'month':
                                    return /*#__PURE__*/ _jsx(SpecraInput, {
                                        id: id,
                                        required: false,
                                        invalid: hasError,
                                        type: "month",
                                        value: rhf.value ?? '',
                                        onChange: (e)=>rhf.onChange(e.target.value),
                                        onBlur: rhf.onBlur,
                                        "data-testid": field.testId
                                    });
                                case 'currency':
                                    return /*#__PURE__*/ _jsx(SpecraInput, {
                                        id: id,
                                        required: false,
                                        invalid: hasError,
                                        type: "number",
                                        value: rhf.value ?? '',
                                        onChange: (e)=>rhf.onChange(e.target.value === '' ? undefined : Number(e.target.value)),
                                        onBlur: (e)=>{
                                            if (e.target.value === '') {
                                                rhf.onBlur();
                                                return;
                                            }
                                            rhf.onChange(Math.round(Number(e.target.value) * 100) / 100);
                                            rhf.onBlur();
                                        },
                                        min: field.min,
                                        max: field.max,
                                        step: ".01"
                                    });
                                case 'select':
                                    if (field.searchable) {
                                        return /*#__PURE__*/ _jsx(SpecraCombobox, {
                                            id: id,
                                            label: field.label,
                                            options: [
                                                ...field.options
                                            ],
                                            value: rhf.value || undefined,
                                            onValueChange: (v)=>{
                                                rhf.onChange(v ?? '');
                                                rhf.onBlur();
                                            },
                                            // Touched when it closes without a pick too, as the plain select is.
                                            onOpenChange: (open)=>{
                                                if (!open) rhf.onBlur();
                                            },
                                            placeholder: "Select...",
                                            searchPlaceholder: `Search ${field.label.toLowerCase()}…`,
                                            triggerProps: {
                                                'aria-invalid': hasError || undefined,
                                                className: invalidClass,
                                                'data-testid': field.testId
                                            }
                                        });
                                    }
                                    // The Radix Select stays (a retained foundation): the native
                                    // SpecraSelect atom has a different interaction contract, and
                                    // every consumer test drives the listbox pattern.
                                    return /*#__PURE__*/ _jsxs(Select, {
                                        required: false,
                                        value: rhf.value ?? '',
                                        onValueChange: (v)=>{
                                            rhf.onChange(v);
                                            // Select doesn't fire onBlur the way text inputs do —
                                            // fire it manually on pick so onTouched validation kicks
                                            // in (and clears any existing error in the same tick).
                                            rhf.onBlur();
                                        },
                                        // Also mark the field touched when the dropdown closes
                                        // without a pick (user opened it, clicked outside).
                                        onOpenChange: (open)=>{
                                            if (!open) rhf.onBlur();
                                        },
                                        children: [
                                            /*#__PURE__*/ _jsx(SelectTrigger, {
                                                id: id,
                                                "aria-invalid": hasError || undefined,
                                                className: invalidClass,
                                                "data-testid": field.testId,
                                                children: /*#__PURE__*/ _jsx(SelectValue, {
                                                    placeholder: "Select..."
                                                })
                                            }),
                                            /*#__PURE__*/ _jsx(SelectContent, {
                                                children: field.options.map((opt)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                        value: opt.value,
                                                        children: opt.label
                                                    }, opt.value))
                                            })
                                        ]
                                    });
                                case 'checkbox':
                                    return /*#__PURE__*/ _jsx(SpecraCheckbox, {
                                        id: id,
                                        checked: Boolean(rhf.value),
                                        onCheckedChange: (checked)=>rhf.onChange(Boolean(checked)),
                                        "aria-invalid": hasError || undefined,
                                        // Checkbox's default state-border classes win over a plain
                                        // border-destructive override, so when invalid we also
                                        // neutralize the data-[state]:border-* variants.
                                        className: hasError ? 'border-destructive focus-visible:ring-destructive/30 data-[state=checked]:border-destructive data-[state=unchecked]:border-destructive' : ''
                                    });
                            }
                        })()
                    }),
                    field.kind === 'select' && field.optionHelp && // Lives outside the Select/SelectContent tree on purpose — see
                    // the optionHelp comment on SelectFieldConfig.
                    /*#__PURE__*/ _jsx("dl", {
                        className: "space-y-0.5 text-xs text-muted-foreground",
                        "data-testid": field.optionHelpTestId,
                        children: field.optionHelp.map((entry)=>/*#__PURE__*/ _jsxs("div", {
                                className: "flex gap-1",
                                children: [
                                    /*#__PURE__*/ _jsxs("dt", {
                                        className: "font-medium",
                                        children: [
                                            entry.label,
                                            ":"
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx("dd", {
                                        children: entry.text
                                    })
                                ]
                            }, entry.label))
                    }),
                    field.kind === 'text' && field.suggestions && // Outside SpecraFormField: it clones props onto its single child,
                    // and a fragment can't receive the id/aria wiring. datalist works
                    // from anywhere in the document via the input's `list` attribute.
                    /*#__PURE__*/ _jsx("datalist", {
                        id: `${id}-suggestions`,
                        children: field.suggestions.map((s)=>/*#__PURE__*/ _jsx("option", {
                                value: s
                            }, s))
                    })
                ]
            });
        }
    });
}
